import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../contexts/NotificationContext';
import { resolveAvatarUrl } from '../utils/imageUrl';
import { supabase } from '../supabaseClient';
import FlagIcon from './FlagIcon';
import Avatar from './Avatar';

const DISPLAY_DURATION_MS = 5000;
const ANIMATION_DURATION_MS = 300;

export default function GlobalNotificationBanner() {
  const { latestArrival, clearLatestArrival, markAsRead } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [isVisible, setIsVisible] = useState(false);
  const [currentNotif, setCurrentNotif] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarSelected, setAvatarSelected] = useState(false);
  const [flagCode, setFlagCode] = useState(null);

  const showTimerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const cleanupTimerRef = useRef(null);

  // 1. 알림 수신 및 배너 표시 타이머 제어
  useEffect(() => {
    // 이전 실행 중인 타이머 일괄 해제
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);

    if (latestArrival) {
      setCurrentNotif(latestArrival);
      setAvatarUrl(null);
      setAvatarSelected(false);
      setFlagCode(null);

      // 마운트 후 프레임에서 애니메이션 등장 트리거
      showTimerRef.current = setTimeout(() => {
        setIsVisible(true);
      }, 20);

      // 자동 닫힘 타이머 (5,000ms)
      hideTimerRef.current = setTimeout(() => {
        setIsVisible(false);
        cleanupTimerRef.current = setTimeout(() => {
          if (latestArrival.type === 'moowoo_gift_expired') {
            markAsRead(latestArrival.aggregated ? latestArrival.ids : latestArrival.id);
          } else if (latestArrival.type === 'moowoo_gift_rejected') {
            markAsRead(latestArrival.id);
          }
          clearLatestArrival();
          setCurrentNotif(null);
        }, ANIMATION_DURATION_MS);
      }, DISPLAY_DURATION_MS);
    } else if (currentNotif) {
      // 외부에서 latestArrival이 null로 변경되었을 때 퇴장 애니메이션 처리
      setIsVisible(false);
      cleanupTimerRef.current = setTimeout(() => {
        setCurrentNotif(null);
      }, ANIMATION_DURATION_MS);
    }

    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    };
  }, [latestArrival, clearLatestArrival, markAsRead]);

  // 2. 초대한 사람의 프로필 아바타 및 국적 비동기 조회 (초대 알림에 한함)
  useEffect(() => {
    if (!currentNotif || currentNotif.type === 'moowoo_gift_expired' || currentNotif.type === 'moowoo_gift_rejected') {
      setAvatarUrl(null);
      setAvatarSelected(false);
      setFlagCode(null);
      return;
    }

    const actorId = currentNotif.actor_id;
    // (가) actor_id 가 없으면 조회를 시도하지 않고 빈 값 처리
    if (!actorId) {
      setAvatarUrl(null);
      setAvatarSelected(false);
      setFlagCode(null);
      return;
    }

    let cancelled = false;

    const fetchProfile = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar, flag_code, avatar_selected')
          .eq('id', actorId)
          .maybeSingle();

        if (cancelled) return;

        // (나) 조회 에러 시에도 배너는 정상 표시 (콘솔 경고 후 빈 값 유지)
        if (error) {
          console.warn('[GlobalNotificationBanner] Error fetching actor profile:', error);
          setAvatarUrl(null);
          setAvatarSelected(false);
          setFlagCode(null);
        } else {
          setAvatarUrl(data?.avatar ? resolveAvatarUrl(data.avatar) : null);
          setAvatarSelected(data?.avatar_selected ?? false);
          setFlagCode(data?.flag_code || null);
        }
      } catch (err) {
        if (cancelled) return;
        // (나) 예외 발생 시에도 빈 값 유지
        console.warn('[GlobalNotificationBanner] Exception fetching actor profile:', err);
        setAvatarUrl(null);
        setAvatarSelected(false);
        setFlagCode(null);
      }
    };

    fetchProfile();

    return () => {
      // (다) useEffect cleanup 에서 취소 플래그로 배너 닫힘 후 응답 상태 반영 차단
      cancelled = true;
    };
  }, [currentNotif]);

  if (!currentNotif) return null;

  const isMoowooExpired = currentNotif.type === 'moowoo_gift_expired';
  const isMoowooRejected = currentNotif.type === 'moowoo_gift_rejected';
  const isMoowooGift = isMoowooExpired || isMoowooRejected;

  // 문구 구성
  const actorName = currentNotif.payload?.actor_name?.trim() || '';
  let titleText = '';
  let displayRoomTitle = '';

  if (isMoowooExpired) {
    const amount = currentNotif.aggregated
      ? currentNotif.totalAmount
      : Number(currentNotif.payload?.amount) || 0;
    titleText = t('notification.moowoo_gift_expired', { amount });
  } else if (isMoowooRejected) {
    const remainingCount = Number(currentNotif.remainingCount) || 0;
    if (remainingCount > 0) {
      titleText = t('notification.moowoo_gift_rejected_more', { name: actorName, count: remainingCount });
    } else {
      titleText = t('notification.moowoo_gift_rejected', { name: actorName });
    }
  } else {
    const rawRoomTitle = currentNotif.payload?.room_title?.trim() || '';
    const isGroup = currentNotif.payload?.is_group !== false;

    if (actorName) {
      titleText = isGroup
        ? t('notification.group_invite_received_group', { name: actorName })
        : t('notification.group_invite_received_direct', { name: actorName });
    } else {
      titleText = t('notification.group_invite_received_anonymous');
    }

    displayRoomTitle = rawRoomTitle || t('notification.unnamed_room');
  }

  const handleBannerClick = () => {
    if (isMoowooExpired) {
      markAsRead(currentNotif.aggregated ? currentNotif.ids : currentNotif.id);
      setIsVisible(false);
      clearLatestArrival();
      if (location.pathname !== '/moowoo') {
        navigate('/moowoo');
      }
    } else if (isMoowooRejected) {
      markAsRead(currentNotif.id);
      setIsVisible(false);
      clearLatestArrival();
      if (location.pathname !== '/moowoo') {
        navigate('/moowoo');
      }
    } else {
      setIsVisible(false);
      clearLatestArrival();
      if (location.pathname !== '/group-chats') {
        navigate('/group-chats', {
          state: { pendingInviteId: currentNotif?.conversation_id },
        });
      }
    }
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    if (isMoowooExpired) {
      markAsRead(currentNotif.aggregated ? currentNotif.ids : currentNotif.id);
    } else if (isMoowooRejected) {
      markAsRead(currentNotif.id);
    }
    setIsVisible(false);
    if (cleanupTimerRef.current) clearTimeout(cleanupTimerRef.current);
    cleanupTimerRef.current = setTimeout(() => {
      clearLatestArrival();
    }, ANIMATION_DURATION_MS);
  };

  return (
    <div className="fixed inset-x-0 top-[calc(env(safe-area-inset-top,0px)+76px+var(--temp-banner-height,0px)+var(--temp-banner-gap,6px))] z-[55] px-4 pointer-events-none">
      <div
        onClick={handleBannerClick}
        className={`w-full max-w-[448px] mx-auto pointer-events-auto bg-surface-container-lowest text-on-surface border border-outline-variant/30 rounded-2xl shadow-xl p-3 flex items-center gap-3 cursor-pointer select-none transition-all duration-300 ease-out ${
          isVisible
            ? 'opacity-100 translate-y-0 scale-100'
            : 'opacity-0 -translate-y-4 scale-95 pointer-events-none'
        }`}
        role="alert"
      >
        {/* 1. 왼쪽: 아바타 또는 무우 선물 아이콘 */}
        <div className="relative flex-shrink-0">
          {isMoowooGift ? (
            <div className="w-10 h-10 rounded-full flex-shrink-0 bg-primary/10 text-primary flex items-center justify-center overflow-hidden border border-primary/20">
              <span
                className="material-symbols-outlined text-primary text-[22px] leading-none select-none"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                card_giftcard
              </span>
            </div>
          ) : (
            <Avatar
              userId={currentNotif.actor_id}
              name={actorName}
              avatarUrl={avatarUrl}
              avatarSelected={avatarSelected}
              size="w-10 h-10"
              className="border border-primary/20"
            />
          )}
          {!isMoowooGift && flagCode && (
            <div className="absolute -bottom-1 -right-1 bg-surface-container-lowest rounded-full p-[2px] shadow-sm flex items-center justify-center pointer-events-none">
              <FlagIcon code={flagCode} className="w-4 h-3 rounded-full object-cover" />
            </div>
          )}
        </div>

        {/* 2. 가운데: 문구 */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-on-surface truncate leading-tight">
            {titleText}
          </p>
          {!isMoowooGift && displayRoomTitle && (
            <p className="text-xs text-on-surface-variant truncate mt-0.5 leading-tight">
              {displayRoomTitle}
            </p>
          )}
        </div>

        {/* 3. 오른쪽: 닫기(X) 버튼 */}
        <button
          type="button"
          onClick={handleCloseClick}
          aria-label={t('notification.close')}
          className="w-8 h-8 flex-shrink-0 flex items-center justify-center text-on-surface-variant/70 hover:text-on-surface hover:bg-surface-container-high rounded-full transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
